import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/endpoints';

/**
 * Data source as returned by the API
 */
export interface DataSourceResponse {
  source_id: string;
  name: string;
  description: string | null;
  source_type: string;
  connection_config: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  metadata: Record<string, unknown>;
}

/**
 * Hook to fetch list of data sources
 * Note: The backend returns a flat array, so we normalize it to match
 * what the UI components expect
 */
export const useSources = (params?: { page?: number; limit?: number }) => {
  return useQuery({
    queryKey: ['sources', params],
    queryFn: async () => {
      const response = await api.sources.list(params);
      const sources = response.data as DataSourceResponse[];

      // The API returns a flat array, but the UI expects a paginated response
      // Normalize the response format
      return {
        items: sources,
        total: sources.length,
        pages: 1,
        page: params?.page || 1,
        limit: params?.limit || 100,
      };
    },
  });
};

/**
 * Hook to fetch a single data source by ID
 */
export const useSource = (id: string) => {
  return useQuery({
    queryKey: ['sources', id],
    queryFn: async () => {
      const response = await api.sources.get(id);
      return response.data;
    },
    enabled: !!id,
  });
};

/**
 * Hook to create a new data source
 */
export const useCreateSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await api.sources.create(data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate sources list to refetch
      queryClient.invalidateQueries({ queryKey: ['sources'] });
    },
  });
};

/**
 * Hook to update a data source
 */
export const useUpdateSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.sources.update(id, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific source
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      queryClient.invalidateQueries({ queryKey: ['sources', variables.id] });
    },
  });
};

/**
 * Hook to delete a data source
 */
export const useDeleteSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.sources.delete(id);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
    },
  });
};

/**
 * Hook to test a data source connection
 */
export const useTestSource = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.sources.test(id);
      return response.data;
    },
  });
};
